import React from 'react';
import { MSL_TABLE_HEADER_SUBTEXT } from '@constants/commonConstants';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import InnerTableHeader from '@molecules/msl/components/forecast/innerTableHeader';

function formatCurrencyWithoutCents(amount) {
  // Convert to number (ensure it handles cases like "0.000", "1200.00")
  const numericValue = parseFloat(amount); // Default to 0 if null or invalid
  // Handle the special case for 0.000 to show "$0"
  if (numericValue === 0) {
    return '$0';
  }
  // Remove the decimals by flooring the value to the nearest dollar
  const dollarsOnly = Math.floor(numericValue); // Truncate decimals
  // Format the number as currency with commas (US-style grouping)
  const formattedValue = dollarsOnly.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Prepend the dollar sign
  return `$${formattedValue}`;
}

function getDisplayValues(params, forecastedField, wonField) {
  // Check if the row is a group row
  if (params.node.group) {
    const allLeafChildren = params?.node?.allLeafChildren || [];
    const totalWonAmount = allLeafChildren.reduce(
      (acc, childNode) => acc + (childNode?.data?.[wonField] || 0),
      0,
    );
    const totalForecastedAmount = allLeafChildren.reduce(
      (acc, childNode) => acc + (childNode?.data?.[forecastedField] || 0),
      0,
    );

    // Format and return the group row values
    const formattedGroupValue = `${formatCurrencyWithoutCents(totalWonAmount)} (${formatCurrencyWithoutCents(totalForecastedAmount)})`;
    return formattedGroupValue;
  }

  // For normal rows, format and return the values
  const wonValue = params.data?.[wonField] || 0;
  const forecastedValue = params.data?.[forecastedField] || 0;
  const formattedValue = `${formatCurrencyWithoutCents(wonValue)} (${formatCurrencyWithoutCents(forecastedValue)})`;
  return formattedValue;
}
function wrapLongValues(value) {
  // Handle wrapping for values with 3 or more characters
  if (value.length >= 3) {
    const mainValue = value.slice(0, -4); // All characters except the last one
    const lastValue = value.slice(-4); // The last character

    return (
      <div
        style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          textAlign: 'right',
          lineHeight: '1.2', // Adjust line height to reduce white space
        }}
      >
        {mainValue}
        <br />
        {lastValue}
      </div>
    );
  }

  // Return the value as is if it's less than 3 characters
  return <div style={{ textAlign: 'right' }}>{value}</div>;
}

function wonVsForecasted(params, forecastedField, wonField) {
  // Check if the row is a group row
  if (params.node.group) {
    const formattedGroupValue = getDisplayValues(params, forecastedField, wonField);
    return wrapLongValues(formattedGroupValue);
  }
  const formattedValue = getDisplayValues(params, forecastedField, wonField);
  return wrapLongValues(formattedValue);
}

const StatusCellRenderer = props => {
  if (props.node.group && props.value) {
    const groupStatus = props?.node?.allLeafChildren[1]?.parent?.allLeafChildren[1]?.data?.status;
    return (
      <span
        style={{
          color: '#2E2E38', // Inner text color is always black
          padding: '3px 10px',
          borderRadius: '50px', // Oval border
          border: `1px solid ${props.value === 'Submitted' || props.value === 'N/A' ? '#168736' : '#B9251C'}`, // Border color matches status
        }}
      >
        {groupStatus}
      </span>
    );
  } else {
    // Handle normal row text
    if (!props.value) {
      // If the value is blank, return null or an empty span with no design
      return null;
    }
    return (
      <span
        style={{
          color: '#2E2E38', // Inner text color is always black
          padding: '3px 10px',
          borderRadius: '50px', // Oval border
          border: `1px solid ${props.value === 'Submitted' || props.value === 'N/A' ? '#168736' : '#B9251C'}`, // Border color matches status
        }}
      >
        {props.value}
      </span>
    );
  }
};
const cellTextAligment = { display: 'flex', justifyContent: 'flex-end' };
export const columnDefsKeys2 = [
  {
    headerName: 'Accounts',
    field: 'name',
    filter: 'agTextColumnFilter', // Use the default text filter
    filterValueGetter: params => {
      // For grouped rows, return the group key or a placeholder
      if (params.node.group) {
        return params.node.key; // Use the group key for filtering grouped rows
      }

      // For non-grouped rows, return the account name
      return params.data?.name || ''; // Ensure it returns an empty string if name is undefined
    },
    suppressMovable: true,
    sortable: true,
    rowGroup: true,
    hide: true,
    flex: 1,
    minWidth: 180,
    cellStyle: {
      display: 'flex',
      justifyContent: 'flex-start', // Align to the left
    },
  },
  {
    headerName: 'GCSP',
    field: 'gcsp_email',
    minWidth: 120,
    tooltipField: 'gcsp_email',
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: params => {
      if (params.node.group) {
        // Ensure allLeafChildren exists and has data
        if (params.node.allLeafChildren && params.node.allLeafChildren.length > 0) {
          // Get the first item in the allLeafChildren array and check if category exists
          const firstLeaf = params.node.allLeafChildren[0].data.gcsp;
          return firstLeaf;
        }
        return ''; // Fallback if data is not available
      } else {
        // For normal rows, return an empty string to hide the value
        return '';
      }
    },
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: false,
    },
    filter: 'agTextColumnFilter',
    filterValueGetter: params => {
      // For grouped rows, return the group key or a placeholder
      if (params?.node?.group) {
        return params?.node?.key; // Use the group key for filtering grouped rows
      }

      // For non-grouped rows, return the account name
      return params?.data?.gcsp || ''; // Ensure it returns an empty string if name is undefined
    },
  },
  {
    headerName: 'Value Type',
    field: 'value_type',
    aggFunc: 'first',
    suppressMovable: true,
    tooltipField: 'value_type',
    valueFormatter: params => {
      // Show custom text for group rows
      return params.node.group ? 'Account Totals' : params.value;
    },
    minWidth: 120,
    hide: window.innerWidth < 600,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: false,
      enableMenu: false,
      enableFilter: false,
    },
  },
  {
    headerName: 'Status',
    field: 'status',
    aggFunc: 'last',
    tooltipField: 'status',
    minWidth: 110,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: StatusCellRenderer,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: false,
      enableMenu: false,
      enableFilter: false,
    },
  },
  {
    headerName: 'Assurance',
    field: 'won_assurance',
    sortable: true,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_ACR,
      subText2: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    minWidth: 150,
    hide: window.innerWidth < 600,
    cellRenderer: params => wonVsForecasted(params, 'assurance', 'won_assurance'),
    tooltipValueGetter: params => getDisplayValues(params, 'assurance', 'won_assurance'),
  },
  {
    headerName: 'Consulting',
    field: 'won_consulting',
    minWidth: 150,
    hide: window.innerWidth < 600,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => wonVsForecasted(params, 'consulting', 'won_consulting'),
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => getDisplayValues(params, 'consulting', 'won_consulting'),
  },
  {
    headerName: 'SAT',
    field: 'won_sat',
    minWidth: 150,
    hide: window.innerWidth < 600,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => wonVsForecasted(params, 'sat', 'won_sat'),
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_ACR,
      subText2: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => getDisplayValues(params, 'sat', 'won_sat'),
  },
  {
    headerName: 'Tax',
    field: 'won_tax',
    sortable: true,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    minWidth: 150,
    hide: window.innerWidth < 600,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_ACR,
      subText2: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => wonVsForecasted(params, 'tax', 'won_tax'),
    tooltipValueGetter: params => getDisplayValues(params, 'tax', 'won_tax'),
  },
  {
    headerName: 'ITTS',
    field: 'won_itts',
    minWidth: 150,
    hide: window.innerWidth < 600,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => wonVsForecasted(params, 'itts', 'won_itts'),
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => getDisplayValues(params, 'itts', 'won_itts'),
  },
  {
    headerName: 'EMEIA ACR',
    field: 'won_emiaacr',
    aggFunc: 'sum',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => wonVsForecasted(params, 'emiaacr', 'won_emiaacr'),
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => getDisplayValues(params, 'emiaacr', 'won_emiaacr'),
  },
  {
    headerName: 'Total',
    field: 'won_total',
    aggFunc: 'sum',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => wonVsForecasted(params, 'total', 'won_total'),
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => getDisplayValues(params, 'total', 'won_total'),
  },
];
